import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncValue, useRevalidator } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { useRevalidateOnSync } from '../../../../offline/useRevalidateOnSync';
import { usePollingRevalidation } from '../../../../hooks/usePollingRevalidation';
import { useCloseOrderConfirmation } from '../../../../hooks/useCloseOrderConfirmation';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import {
  usePersistedPageSize,
  PageSizeOption,
} from '../../../../hooks/usePersistedPageSize';
import { PaginationButtons } from '../../../../components/display/tables/components/PaginationButtons';
import { tableIds } from '../../../../components/display/tables/types';
import {
  getCursor,
  getIsLoadingData,
} from '../../../../state/selectors/ui/tables';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { loadingData } from '../../../../state/actions/ui/loadingData';
import { store } from '../../../../state/configureStore';
import { client } from '../../../../gql/client';

import {
  ContainerStyled,
  ContainerLoader,
  ContainerButton,
  ResultsZone,
} from './styles';
import { MainButton } from '../../../../components/inputs/buttons/MainButton';
import { Loader } from '../../../../components/inputs/Loader';

import { FETCH_LABOR_HOURS_REPORT } from '../../../../gql/queries/reporting/laborHoursReport';
import {
  defaultDateRange,
  cursorToFilters,
  filtersToCursor,
  toReportFilter,
} from './utils';
import { FiltersBar, TimeManagementFilters } from './components/FiltersBar';
import { OperatorsHoursTable } from './components/OperatorsHoursTable';
import { sumMinutes } from './utils';
import { generateLaborHoursExcel } from './exports/generateExcel';
import { generateLaborHoursPdf } from './exports/generatePdf';
import { TimeManagementLoaderData } from './types';

/**
 * Página informe Gestió Horària.
 *
 * Arquitectura de carga de datos = COPIA de HistoricalAccidentsReport:
 *   - Los datos (filterOptions + report) los carga el LOADER de React
 *     Router (loader.ts) vía `client.readQuery` cache-first + network
 *     fallback, y se entregan vía `({ data })` → `useAsyncValue`.
 *     Cache hit = pintado instantáneo SIN spinner (igual que las otras
 *     tablas al volver a entrar).
 *   - El refetch (cambiar filtros / botón refresh / polling) hace
 *     `client.query(network-only)` + `revalidator.revalidate()` con el
 *     flag Redux `loadingData` para mostrar el spinner SOLO durante el
 *     refetch (no en la carga inicial de cache).
 *   - Los filtros persisten en el cursor Redux
 *     `tableIds.REPORTING_TIME_MANAGEMENT`.
 *
 * Requiere estar envuelta en `<Fallback dependsOn="data">` en el router
 * (igual que HistoricalActions/HistoricalAccidents) — el Fallback hace el
 * `<Suspense><Await resolve={loaderData.data}>`.
 */
export default function TimeManagementPage(): JSX.Element {
  const { t } = useTranslation([
    'forms',
    'buttons',
    'common',
    'tables',
    'toasts',
  ]);
  const dispatch = useDispatch();
  const revalidator = useRevalidator();
  // Re-ejecuta el loader cuando sync offline → online completa.
  useRevalidateOnSync();

  // T99: Modal de cerrar-OT. Al validar la última actuación desde el informe y
  // volver aquí (returnTo=/reporting/time-management) salta el modal de cerrar
  // la OT, con la info de la OT resuelta (roid/tipo/descripción). Antes no había
  // listener en esta pantalla → el modal nunca aparecía desde Gestión Horaria.
  useCloseOrderConfirmation();

  // Feature flag de tipos de hora (mismo que la tabla): gatea el desglose por
  // tipo en los exports Excel/PDF.
  const showHoursType = useFeatureFlag('showHoursType', true);

  const data = useAsyncValue() as TimeManagementLoaderData;
  const isLoadingData = useSelector(
    getIsLoadingData(tableIds.REPORTING_TIME_MANAGEMENT),
  );

  const initial = defaultDateRange();
  const filterOptions = data?.filterOptions ?? { teams: [], operators: [] };
  const rows = data?.report?.rows ?? [];
  const activeFilters: TimeManagementFilters =
    data?.filters ?? cursorToFilters(null, initial);

  const teamNameById: Record<string, string> = Object.fromEntries(
    (filterOptions.teams ?? []).map((tm) => [tm.id, tm.name]),
  );

  const showLoader = Boolean(isLoadingData);
  const exportsDisabled = showLoader || rows.length === 0;

  // Paginación CLIENT-SIDE: el report es agregado (1 fila por operario/
  // empresa) y el backend `laborHoursReport` devuelve TODAS las filas de
  // golpe (sin cursor, por diseño D5 del spec — dataset pequeño ~50-200
  // operarios). Para que la UI sea idéntica a las otras tablas, reutilizo
  // el componente canónico `PaginationButtons` + `usePersistedPageSize`
  // (mismo selector "Filas por página", mismo take=25, mismo contador),
  // pero la página se calcula con un slice local en vez de una llamada
  // server-side por página.
  const { pageSize, setPageSize, availableOptions } = usePersistedPageSize(
    tableIds.REPORTING_TIME_MANAGEMENT,
  );
  const [currentPage, setCurrentPage] = useState(1);

  // Feedback de exportación (Gestión Horaria no usa modal): deshabilita ambos
  // botones y el pulsado muestra "Procesando…" mientras se genera el Excel/PDF
  // (el PDF hace toBlob, que tarda/bloquea en informes grandes).
  const [exporting, setExporting] = useState<null | 'excel' | 'pdf'>(null);
  const runExport = useCallback(
    async (
      kind: 'excel' | 'pdf',
      fn: () => void | Promise<void>,
    ): Promise<void> => {
      setExporting(kind);
      try {
        await fn();
      } finally {
        setExporting(null);
      }
    },
    [],
  );

  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));

  // Reset a la página 1 cuando cambian los datos (nueva búsqueda) o el
  // tamaño de página, para no quedar en una página fuera de rango.
  useEffect(() => {
    setCurrentPage(1);
  }, [rows.length, pageSize]);

  // Ordena interno-primero para que la agrupación por secciones de la tabla
  // sea coherente entre páginas (primero todos los internos, luego externos).
  const sortedRows = useMemo(
    () =>
      [...rows].sort(
        (a, b) =>
          (a.kind === 'external-estimated' ? 1 : 0) -
          (b.kind === 'external-estimated' ? 1 : 0),
      ),
    [rows],
  );

  const pagedRows = useMemo(
    () =>
      sortedRows.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [sortedRows, currentPage, pageSize],
  );

  // Totales SEPARADOS por naturaleza (globales, sobre TODAS las filas, no la
  // página): reales internas vs estimadas externas. Nunca un total mixto.
  // Se pasan a la tabla para pintarse en el pie (`tfoot`), estilo nativo.
  const summary = useMemo(() => {
    const internal = rows.filter((r) => r.kind !== 'external-estimated');
    const external = rows.filter((r) => r.kind === 'external-estimated');
    return {
      internalMinutes: sumMinutes(internal),
      externalMinutes: sumMinutes(external),
      internalCount: internal.length,
      externalCount: external.length,
    };
  }, [rows]);

  // Refetch network-only + revalidate (canónico HistoricalAccidents
  // onReFetchData). Lee el cursor actual de Redux para las variables.
  const onReFetchData = useCallback(async () => {
    const cursor = getCursor(tableIds.REPORTING_TIME_MANAGEMENT)(
      store.getState(),
    );
    const filters = cursorToFilters(cursor, initial);
    dispatch(loadingData(tableIds.REPORTING_TIME_MANAGEMENT, true));
    try {
      await client.query({
        query: FETCH_LABOR_HOURS_REPORT,
        variables: { filter: toReportFilter(filters) },
        fetchPolicy: 'network-only',
      });
    } finally {
      dispatch(loadingData(tableIds.REPORTING_TIME_MANAGEMENT, false));
    }
    revalidator.revalidate();
  }, [dispatch, revalidator, initial]);

  const handleSearch = useCallback(
    (next: TimeManagementFilters) => {
      dispatch(setCursor(tableIds.REPORTING_TIME_MANAGEMENT, filtersToCursor(next)));
      onReFetchData();
    },
    [dispatch, onReFetchData],
  );

  const handleClear = useCallback(
    (reset: TimeManagementFilters) => {
      dispatch(
        setCursor(tableIds.REPORTING_TIME_MANAGEMENT, filtersToCursor(reset)),
      );
      onReFetchData();
    },
    [dispatch, onReFetchData],
  );

  const handleRefresh = useCallback(() => {
    onReFetchData();
  }, [onReFetchData]);

  // Polling 5min (canónico HistoricalAccidents onPollingRefresh):
  // refresca datos en cache sin tocar el cursor + revalida.
  const onPollingRefresh = useCallback(async () => {
    const cursor = getCursor(tableIds.REPORTING_TIME_MANAGEMENT)(
      store.getState(),
    );
    const filters = cursorToFilters(cursor, initial);
    await client.query({
      query: FETCH_LABOR_HOURS_REPORT,
      variables: { filter: toReportFilter(filters) },
      fetchPolicy: 'network-only',
    });
    revalidator.revalidate();
  }, [revalidator, initial]);
  usePollingRevalidation(onPollingRefresh);

  return (
    <ContainerStyled>
      <FiltersBar
        initialFilters={activeFilters}
        defaultFromDate={initial.fromDate}
        defaultToDate={initial.toDate}
        teamOptions={filterOptions.teams ?? []}
        userOptions={filterOptions.operators ?? []}
        externalCompanyOptions={filterOptions.externalCompanies ?? []}
        isLoading={showLoader}
        onSearch={handleSearch}
        onClear={handleClear}
        onRefresh={handleRefresh}
      />

      <ResultsZone>
        {showLoader && (
          <ContainerLoader>
            <Loader />
          </ContainerLoader>
        )}
        <OperatorsHoursTable
          rows={pagedRows}
          teamNameById={teamNameById}
          isLoading={showLoader}
          summary={summary}
        />
      </ResultsZone>

      <PaginationButtons
        pageCount={pageCount}
        currentPage={currentPage}
        totalCount={rows.length}
        nextPage={() => setCurrentPage((p) => Math.min(p + 1, pageCount))}
        previousPage={() => setCurrentPage((p) => Math.max(1, p - 1))}
        isDisabled={showLoader}
        pageSize={pageSize as PageSizeOption}
        onPageSizeChange={setPageSize}
        pageSizeOptions={availableOptions}
      />

      <ContainerButton data-testid="time-management-actions">
        <MainButton
          type="button"
          text={
            exporting === 'excel'
              ? t('toasts:info.processing')
              : t('buttons:exportExcel')
          }
          disabled={exportsDisabled || exporting !== null}
          onClick={() =>
            // Combina: wrapper runExport + filtro states (#51 gestión-horaria) CON
            // rows: sortedRows (mi fix) para que el Excel salga en el mismo orden
            // que la tabla (internos primero).
            runExport('excel', () =>
              generateLaborHoursExcel({
                rows: sortedRows,
                teamNameById,
                filters: {
                  fromDate: activeFilters.fromDate,
                  toDate: activeFilters.toDate,
                  states: activeFilters.states,
                },
                showHoursType,
                t,
              }),
            )
          }
          data-testid="export-excel-btn"
        />
        <MainButton
          type="button"
          text={
            exporting === 'pdf'
              ? t('toasts:info.processing')
              : t('buttons:exportPdf')
          }
          disabled={exportsDisabled || exporting !== null}
          onClick={() =>
            // Combina: wrapper runExport + filtro states (#51) CON rows: sortedRows
            // (mi fix) para que el PDF respete el orden de la tabla.
            runExport('pdf', () =>
              generateLaborHoursPdf({
                rows: sortedRows,
                teamNameById,
                filters: {
                  fromDate: activeFilters.fromDate,
                  toDate: activeFilters.toDate,
                  states: activeFilters.states,
                },
                showHoursType,
                t,
              }),
            )
          }
          data-testid="export-pdf-btn"
        />
      </ContainerButton>
    </ContainerStyled>
  );
}
